import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { PageContainer, Section } from '../components/AdminContentWrapper';
import { panelSx } from '../components/adminStyles';
import HelpIcon from '../../../components/help/HelpIcon';
import TablePaginationBar from '../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../hooks';
import { DataContext } from '../../../context/DataContext';
import AuditService from '../../../services/AuditService';
import { formatCredits } from '../../../utils/answerCredits';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

export const PREVIEW_LENGTH = 30;

export const truncate = (text, max = PREVIEW_LENGTH) => {
  if (!text) return '—';
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const formatDuration = (ms) => {
  const n = Number(ms) || 0;
  return n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${n} ms`;
};

const pad2 = (n) => String(n).padStart(2, '0');

// DD/MM/YYYY HH:MM (24 h, hora local del navegador).
export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// El runtime guarda "unknown" cuando el token no trae el dato.
const known = (value) => (value && value !== 'unknown' ? value : null);

export const userLabel = (item) => known(item.email) || known(item.username) || item.user_id || '—';

// Turno + gráfica. Registros anteriores a total_credits solo traen el turno.
export const totalCredits = (item) => {
  const total = typeof item.total_credits === 'number' ? item.total_credits : item.credits_consumed;
  return typeof total === 'number' ? formatCredits(total) : '—';
};

// Modal genérico de "ver completo" -- misma paleta que WorkspaceDetailDialog
// (fondo #0f172a, borde sutil), usado para pregunta, query, respuesta y
// contexto enviado.
const ViewFullTextDialog = ({ open, title, content, monospace, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface)', border: '1px solid var(--wb-border-16)' } }}
  >
    <DialogTitle sx={{ color: 'var(--wb-text-title)', pr: 6 }}>
      {title}
      <IconButton
        aria-label="Cerrar"
        onClick={onClose}
        sx={{ position: 'absolute', right: 16, top: 16, color: 'var(--wb-muted-80)' }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
      <Typography
        variant="body2"
        component={monospace ? 'pre' : 'p'}
        sx={{
          m: 0,
          color: 'var(--wb-text)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          ...(monospace && { fontFamily: 'monospace', fontSize: '0.8rem' })
        }}
      >
        {content || 'Sin contenido.'}
      </Typography>
    </DialogContent>
  </Dialog>
);

// Un dato de la tarjeta: etiqueta pequeña arriba, valor abajo.
const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      component="div"
      sx={{ color: 'var(--wb-muted-70)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.25 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const valueSx = { color: 'var(--wb-text)', wordBreak: 'break-word', fontSize: '0.8125rem' };

// Primeros PREVIEW_LENGTH caracteres + ícono (al final del texto, no en
// columna aparte, para no quitarle ancho) para verlo completo.
const PreviewValue = ({ text, title, monospace, onView }) => (
  <Typography
    variant="body2"
    title={text || undefined}
    sx={{ ...valueSx, ...(monospace && { fontFamily: 'monospace', fontSize: '0.75rem' }) }}
  >
    {truncate(text)}
    {text && (
      <Tooltip title={`Ver ${title.toLowerCase()} completa`}>
        <IconButton
          size="small"
          aria-label={`Ver ${title.toLowerCase()} completa`}
          onClick={() => onView(`${title} completa`, text, monospace)}
          sx={{ color: 'var(--wb-blue)', p: 0.25, ml: 0.25, verticalAlign: 'text-bottom' }}
        >
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    )}
  </Typography>
);

// Columnas de la tarjeta en pantallas anchas: todas en una sola fila. En
// pantallas más angostas los datos pasan a una cuadrícula de 2-3 columnas.
// Fecha, tiempo, créditos y contexto se ajustan a su contenido; el resto
// reparte el ancho que queda.
const ROW_COLUMNS = 'max-content minmax(0, 1.2fr) repeat(3, minmax(0, 1.4fr)) minmax(0, 1.2fr) minmax(0, 1fr) max-content max-content';

const AuditRecordCard = ({ item, showContext, onView }) => (
  <Box
    data-testid="audit-record-card"
    sx={{
      ...panelSx,
      boxShadow: 'none',
      backgroundColor: 'var(--wb-bg-surface)',
      p: 1.5,
      display: 'grid',
      columnGap: 1.5,
      rowGap: 1.5,
      alignItems: 'start',
      gridTemplateColumns: {
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: 'repeat(3, minmax(0, 1fr))',
        lg: showContext ? `${ROW_COLUMNS} auto` : ROW_COLUMNS
      },
      '&:hover': { borderColor: 'var(--wb-blue-40)' }
    }}
  >
    <Field label="Fecha">
      <Typography variant="body2" sx={{ ...valueSx, whiteSpace: 'nowrap' }}>
        {formatDateTime(item.created_date)}
      </Typography>
    </Field>
    <Field label="Usuario">
      <Typography variant="body2" noWrap title={userLabel(item)} sx={valueSx}>
        {userLabel(item)}
      </Typography>
    </Field>
    <Field label="Pregunta">
      <PreviewValue text={item.prompt} title="Pregunta" onView={onView} />
    </Field>
    <Field label="Query">
      <PreviewValue text={item.query_text} title="Query" monospace onView={onView} />
    </Field>
    <Field label="Respuesta">
      <PreviewValue text={item.response_text} title="Respuesta" onView={onView} />
    </Field>
    <Field label="Modelo">
      {/* Los ids de modelo son largos: una línea y el valor completo al pasar el cursor. */}
      <Typography variant="body2" noWrap title={item.model_id || undefined} sx={valueSx}>
        {item.model_id || '—'}
      </Typography>
    </Field>
    <Field label="Agente">
      <Chip
        size="small"
        label={item.agent_name || '—'}
        title={item.agent_name || undefined}
        sx={{ maxWidth: '100%', backgroundColor: 'var(--wb-accent-tint)', color: 'var(--wb-accent)', fontWeight: 500 }}
      />
    </Field>
    <Field label="Tiempo">
      <Typography variant="body2" sx={{ ...valueSx, whiteSpace: 'nowrap' }}>
        {formatDuration(item.duration_ms)}
      </Typography>
    </Field>
    <Field label="Créditos">
      <Typography variant="body2" sx={{ ...valueSx, whiteSpace: 'nowrap', fontWeight: 600 }}>
        {totalCredits(item)}
      </Typography>
    </Field>
    {showContext && (
      <Field label="Contexto">
        {item.context_sent ? (
          <Tooltip title="Ver contexto enviado">
            <IconButton
              size="small"
              aria-label="Ver contexto enviado"
              onClick={() => onView('Contexto enviado completo', item.context_sent)}
              sx={{ color: 'var(--wb-blue)', p: 0.25 }}
            >
              <VisibilityIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={valueSx}>—</Typography>
        )}
      </Field>
    )}
  </Box>
);

// Indicadores de costo promedio, en el orden en que se muestran.
export const SUMMARY_MODES = [
  { key: 'basic', label: 'Análisis básico' },
  { key: 'deep', label: 'Análisis profundo' },
  { key: 'dashboard', label: 'Dashboard' }
];

// Promedios chicos (p. ej. Haiku) no deben verse como 0.00.
export const formatAverageCredits = (value) => {
  if (typeof value !== 'number') return '—';
  if (value > 0 && value < 0.01) {
    return value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }
  return formatCredits(value);
};

export const CostSummaryCards = ({ summary, loading, error }) => (
  <Box sx={{ mb: 3 }}>
    <Box
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}
    >
      {SUMMARY_MODES.map(({ key, label }) => {
        const mode = summary?.modes?.[key];
        const count = mode?.count || 0;
        return (
          <Box key={key} data-testid={`cost-summary-${key}`} sx={{ ...panelSx, p: 2 }}>
            <Typography variant="body2" sx={{ color: 'var(--wb-text-label)', fontWeight: 600 }}>
              {label}
            </Typography>
            {loading ? (
              <CircularProgress size={20} sx={{ color: 'var(--wb-accent)', mt: 1.5 }} />
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: 'var(--wb-text-title)', fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.2 }}>
                    {count ? formatAverageCredits(mode.avg_credits) : '—'}
                  </Typography>
                  {count > 0 && (
                    <Typography variant="body2" sx={{ color: 'var(--wb-text-label)' }}>
                      créditos por pregunta
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--wb-text-label)' }}>
                  {count
                    ? `${count} ${count === 1 ? 'pregunta' : 'preguntas'} · últimos ${summary.periodDays} días`
                    : `Sin preguntas en ${summary?.periodDays || 30} días`}
                </Typography>
              </>
            )}
          </Box>
        );
      })}
    </Box>
    {error && (
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--wb-warning)' }}>
        {error}
      </Typography>
    )}
  </Box>
);

const AuditQueries = () => {
  const { isPlatformRoot, isTenantAdmin } = useContext(DataContext);

  const [questionFilter, setQuestionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [canViewAll, setCanViewAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [viewDialog, setViewDialog] = useState({ open: false, title: '', content: '', monospace: false });

  // El backend solo manda el contexto al root; esto además oculta el campo.
  const showContext = Boolean(isPlatformRoot);
  const activeFilters = [questionFilter, userFilter, modelFilter].filter((v) => v.trim()).length;

  const loadSummary = useCallback(async (filters) => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      setSummary(await AuditService.getSummary(filters));
    } catch (err) {
      setSummary(null);
      setSummaryError('No se pudo calcular el costo promedio.');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const filters = {
      question: questionFilter.trim() || undefined,
      userId: userFilter.trim() || undefined,
      model: modelFilter.trim() || undefined
    };
    // Los indicadores usan los mismos filtros; su error no bloquea la lista.
    loadSummary(filters);
    try {
      const result = await AuditService.listInvocations(filters);
      setItems(result.items);
      setNextCursor(result.nextCursor);
      setCanViewAll(result.canViewAll);
    } catch (err) {
      setError(err.message || 'No fue posible obtener las consultas de auditoría.');
    } finally {
      setIsLoading(false);
    }
  }, [questionFilter, userFilter, modelFilter, loadSummary]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const result = await AuditService.listInvocations({
        question: questionFilter.trim() || undefined,
        userId: userFilter.trim() || undefined,
        model: modelFilter.trim() || undefined,
        cursor: nextCursor
      });
      setItems((prev) => [...prev, ...result.items]);
      setNextCursor(result.nextCursor);
    } catch (err) {
      setError(err.message || 'No fue posible cargar más resultados.');
    } finally {
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextCursor, isLoadingMore, questionFilter, userFilter, modelFilter]);

  const openView = (title, content, monospace = false) => setViewDialog({ open: true, title, content, monospace });
  const closeView = () => setViewDialog({ open: false, title: '', content: '', monospace: false });

  // `items` acumula lo que ya trajo el cursor del backend; esto pagina esa
  // acumulación en la UI y pide la tanda siguiente cuando el usuario llega
  // al final de lo cargado. El botón "Cargar más" sigue estando para
  // quien prefiera traerlas a mano.
  const { pageRows, paginationProps } = useTablePagination(items, {
    storageKey: 'auditQueries',
    resetDeps: [questionFilter, userFilter, modelFilter],
    hasMore: Boolean(nextCursor),
    isLoadingMore,
    onNeedMore: loadMore
  });

  return (
    <PageContainer
      title="Auditoría de consultas"
      description={
        canViewAll || isPlatformRoot || isTenantAdmin
          ? 'Consultas realizadas por todos los usuarios de este tenant a los agentes de WhisperBI.'
          : 'Tus propias consultas realizadas a los agentes de WhisperBI.'
      }
      actions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={isLoading}
          sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}
        >
          Actualizar
        </Button>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Costo promedio por tipo de pregunta (últimos 30 días, con los filtros) */}
      <CostSummaryCards summary={summary} loading={summaryLoading} error={summaryError} />

      {/* Filtros: colapsados por defecto */}
      <Box sx={{ ...panelSx, mb: 3 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
          aria-controls="audit-filters"
          sx={{
            all: 'unset',
            boxSizing: 'border-box',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, md: 3 },
            py: 1.5,
            cursor: 'pointer',
            color: 'var(--wb-text)',
            '&:focus-visible': { outline: '2px solid var(--wb-blue)', outlineOffset: -2, borderRadius: 2 }
          }}
        >
          <FilterListIcon fontSize="small" sx={{ color: 'var(--wb-muted-70)' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--wb-text)' }}>
            Filtros
          </Typography>
          {activeFilters > 0 && (
            <Chip
              size="small"
              label={`${activeFilters} activo${activeFilters > 1 ? 's' : ''}`}
              sx={{ backgroundColor: 'var(--wb-accent-tint)', color: 'var(--wb-accent)', fontWeight: 500 }}
            />
          )}
          <ExpandMoreIcon
            sx={{
              ml: 'auto',
              color: 'var(--wb-muted-70)',
              transition: 'transform 0.2s',
              transform: filtersOpen ? 'rotate(180deg)' : 'none'
            }}
          />
        </Box>
        <Collapse in={filtersOpen} unmountOnExit={false}>
          <Box id="audit-filters" sx={{ px: { xs: 2, md: 3 }, pb: 2.5, pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                label="Buscar por pregunta"
                value={questionFilter}
                onChange={(e) => setQuestionFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                size="small"
                sx={{ flex: 1, ...darkTextFieldStyle }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpIcon module="agentes" topic="auditoria-consultas" anchor="buscar-por-pregunta" />
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                label="Usuario (user id)"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                size="small"
                sx={{ flex: 1, ...darkTextFieldStyle }}
                disabled={!canViewAll}
                helperText={!canViewAll ? 'Solo administradores pueden filtrar por otro usuario.' : ' '}
                FormHelperTextProps={{ sx: { color: 'var(--wb-muted-60)' } }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpIcon module="agentes" topic="auditoria-consultas" anchor="usuario-user-id" />
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                label="Modelo"
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                size="small"
                sx={{ flex: 1, ...darkTextFieldStyle }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HelpIcon module="agentes" topic="auditoria-consultas" anchor="modelo" />
                    </InputAdornment>
                  )
                }}
              />
              <Button variant="contained" onClick={load} sx={{ bgcolor: 'var(--wb-primary)', color: 'var(--wb-on-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
                Buscar
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Box>

      <Section
        title="Consultas"
        subtitle={
          canViewAll
            ? 'Viendo todas las preguntas del tenant (permiso de administrador).'
            : 'Viendo solo tus propias preguntas.'
        }
        chips={canViewAll ? ['Vista de administrador'] : undefined}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
          </Box>
        ) : items.length === 0 ? (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 3, textAlign: 'center' }}>
            No hay consultas registradas para los filtros seleccionados.
          </Typography>
        ) : (
          <>
            <Stack spacing={1.25}>
              {pageRows.map((item) => (
                <AuditRecordCard
                  key={item.invocation_id}
                  item={item}
                  showContext={showContext}
                  onView={openView}
                />
              ))}
            </Stack>
            <TablePaginationBar {...paginationProps} />
            {nextCursor && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={loadMore}
                  disabled={isLoadingMore}
                  sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}
                >
                  {isLoadingMore ? 'Cargando…' : 'Cargar más'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Section>

      <ViewFullTextDialog
        open={viewDialog.open}
        title={viewDialog.title}
        content={viewDialog.content}
        monospace={viewDialog.monospace}
        onClose={closeView}
      />
    </PageContainer>
  );
};

export default AuditQueries;
